/**
 * Pure, client-safe YMAL mapper.
 *
 * ARCHITECTURE RULE: this module imports NO data. The blog pool comes in as an
 * argument, computed server-side by @/lib/blog/ymal-pool (which imports the
 * 1.27 MB posts module). Server pages call getDynamicYMALItems(getYMALPool(),
 * ...) directly; client components receive either the finished items or the
 * light pool as props from their server page — so the full blog can never leak
 * into a client bundle (enforced by the audit:blog hygiene gate).
 */

export interface YouMightAlsoLikeItem {
  title: string;
  href: string;
  description: string;
  type: "blog" | "calculator" | "comparison" | "glossary";
  image?: { url: string; alt: string };
  date?: string;
  readTime?: number;
  countryCode?: string;
  countryName?: string;
}

/** Card-level projection of a blog post — the only fields YMAL needs. */
export interface YMALPoolItem {
  slug: string;
  title: string;
  summary: string;
  category: string;
  countryCode?: string;
  isCountryGuide?: boolean;
  image?: { url: string; alt: string };
}

function mapPoolToYMALItems(pool: YMALPoolItem[]): YouMightAlsoLikeItem[] {
  return pool.map((rp) => ({
    title: rp.title,
    href: `/blog/${rp.slug}`,
    description: rp.summary || "",
    type: "blog" as const,
    image: rp.image,
  }));
}

function simpleHash(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

export function getDynamicYMALItems(
  pool: YMALPoolItem[],
  seed: string,
  options?: {
    category?: string;
    countryCode?: string;
    excludeSlug?: string;
    limit?: number;
  }
): YouMightAlsoLikeItem[] {
  const limit = options?.limit || 8;
  const excludeSlug = (options?.excludeSlug || "").toLowerCase();
  const cat = (options?.category || "").toLowerCase();
  const ctry = (options?.countryCode || "").toLowerCase();

  // Filter out excluded post/slug and guides
  const available = pool.filter(
    (p) => !p.isCountryGuide && p.slug.toLowerCase() !== excludeSlug
  );

  if (available.length === 0) return [];

  // Group into relevant matches vs general pool
  const relevant: typeof available = [];
  const general: typeof available = [];

  available.forEach((p) => {
    const isCatMatch = Boolean(cat && p.category?.toLowerCase() === cat);
    const isCtryMatch = Boolean(ctry && p.countryCode?.toLowerCase() === ctry);
    if (isCatMatch || isCtryMatch) {
      relevant.push(p);
    } else {
      general.push(p);
    }
  });

  // Hash-based deterministic rotation so each page seed yields distinct items
  const hash = simpleHash(seed);

  const rotateArray = <T>(arr: T[], offset: number): T[] => {
    if (arr.length === 0) return [];
    const start = offset % arr.length;
    return [...arr.slice(start), ...arr.slice(0, start)];
  };

  const rotatedRelevant = rotateArray(relevant, hash);
  const rotatedGeneral = rotateArray(general, hash >> 2);

  // Combine relevant first, then general
  const combined = [...rotatedRelevant, ...rotatedGeneral].slice(0, limit);

  return mapPoolToYMALItems(combined);
}
